// src/pages/Register.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Fade,
  Alert,
  Snackbar,
  TextFieldVariants,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';

const theme = createTheme({
  palette: {
    mode: 'light',
    background: { default: '#ffffff', paper: '#ffffff' },
    primary: { main: '#C4B08C' },
    text: { primary: '#111', secondary: '#555' },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});

const API_URL = process.env.REACT_APP_API_URL;

const Register: React.FC = () => {
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, severity: 'success' as 'success' | 'error', message: '' });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ---- auto-fill from URL ---- */
  const [form, setForm] = useState({
    firstname: searchParams.get('firstname') || '',
    lastname: searchParams.get('lastname') || '',
    phone: searchParams.get('phone') || '',
    email: searchParams.get('email') || '',
    password: '',
    confirm: '',
    id: searchParams.get('id') || '',
  });

  const [errors, setErrors] = useState<any>({});

  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const newErrors: any = {};
    if (!form.firstname.trim()) newErrors.firstname = 'First name required';
    if (!form.lastname.trim()) newErrors.lastname = 'Last name required';
    if (!/^\d{8,15}$/.test(form.phone)) newErrors.phone = '8–15 digits only';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email';
    if (form.password.length < 6) newErrors.password = '≥ 6 characters';
    if (form.password !== form.confirm) newErrors.confirm = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const submit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!validate()) return;
  setLoading(true);

  try {
    const userId = form.id; // backend must return the new user id
    await fetch(`${API_URL}/access/${userId}`, { method: 'DELETE' });
        const createRes = await fetch(`${API_URL}/access/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        phone: form.phone,
        password: form.password,
      }),
    });
    const data = await createRes.json();
    if (!createRes.ok) throw new Error(data.message || 'Server error');




    setAlert({ open: true, severity: 'success', message: 'User registered !' });
    setTimeout(() => navigate('/login'), 1500);
  } catch (err: any) {
    setAlert({ open: true, severity: 'error', message: err.message });
  } finally {
    setLoading(false);
  }
};

  return (
    <ThemeProvider theme={theme}>
      <Fade in timeout={700}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            px: 2,
          }}
        >
          <Paper elevation={0} sx={{ width: '100%', maxWidth: 460, p: 4, borderRadius: 5, boxShadow: '0 8px 40px rgba(0,0,0,.08)' }}>
            <Typography variant="h4" fontWeight={700} textAlign="center" mb={0.5}>
              Join Us
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
              Fill in your information
            </Typography>

            <Box component="form" onSubmit={submit} noValidate>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField 
                  fullWidth 
                  label="First Name" 
                  name="firstname" 
                  value={form.firstname} 
                  onChange={handle} 
                  {...fieldProps} 
                  variant="standard"
                  error={!!errors.firstname} 
                  helperText={errors.firstname} 
                />
                <TextField 
                  fullWidth 
                  label="Last Name" 
                  name="lastname" 
                  value={form.lastname} 
                  onChange={handle} 
                  {...fieldProps} 
                  variant="standard"
                  error={!!errors.lastname} 
                  helperText={errors.lastname} 
                />
              </Box>

              <TextField 
                fullWidth 
                label="Phone" 
                name="phone" 
                value={form.phone} 
                onChange={handle} 
                {...fieldProps} 
                variant="standard"
                error={!!errors.phone} 
                helperText={errors.phone} 
              />
              <TextField 
                fullWidth 
                label="Email" 
                type="email" 
                name="email" 
                value={form.email} 
                onChange={handle} 
                {...fieldProps} 
                variant="standard"
                error={!!errors.email} 
                helperText={errors.email} 
              />
              <TextField<TextFieldVariants>
                fullWidth
                label="Password"
                type={show ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handle}
                {...fieldProps}
                variant="standard"
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShow(!show)} edge="end">
                        {show ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
                <TextField<TextFieldVariants>
                fullWidth
                label="Confirm Password"
                type={showConfirm ? 'text' : 'password'}
                name="confirm"
                value={form.confirm}
                onChange={handle}
                {...fieldProps}
                variant="standard"
                error={!!errors.confirm}
                helperText={errors.confirm}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.4,
                  fontWeight: 600,
                  borderRadius: 3,
                  bgcolor: 'primary.main',
                  color: '#000',
                  '&:hover': { bgcolor: '#b39d77' },
                }}
              >
                {loading ? 'Processing…' : 'Sign Up'}
              </Button>

              <Typography variant="body2" textAlign="center">
                Already have an account?{' '}
                <Link
                  onClick={() => navigate('/login')}
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
                >
                  Login
                </Link>
              </Typography>
            </Box>

            <Snackbar
              open={alert.open}
              autoHideDuration={4000}
              onClose={() => setAlert({ ...alert, open: false })}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert severity={alert.severity} sx={{ width: '100%' }}>
                {alert.message}
              </Alert>
            </Snackbar>
          </Paper>
        </Box>
      </Fade>
    </ThemeProvider>
  );
};

export default Register;

const fieldProps = {
  variant: 'standard',
  InputProps: { disableUnderline: true, sx: { px: 2, py: 1.2, borderRadius: 3, bgcolor: '#f5f5f5' } },
  InputLabelProps: { sx: { ml: 2 } },
};